import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type LanguageCode = "zh" | "en";
export type Translator = (chinese: string, english: string) => string;

const LANGUAGE_STORAGE_KEY = "pacture_lang";

type I18nContextValue = {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: Translator;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function initialLanguage(): LanguageCode {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === "zh" || stored === "en") return stored;
  } catch {
    // Storage can be unavailable in locked-down administrator browsers.
  }
  return "zh";
}

type LocalizedValue = { zh: string; en: string };

export function I18nProvider({
  children,
  title,
  description,
}: {
  children: ReactNode;
  title?: LocalizedValue;
  description?: LocalizedValue;
}) {
  const [language, setLanguageState] = useState<LanguageCode>(initialLanguage);
  const setLanguage = useCallback((next: LanguageCode) => {
    document.documentElement.lang = next === "zh" ? "zh-CN" : "en";
    setLanguageState(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language === "zh" ? "zh-CN" : "en";
    if (title) document.title = title[language];
    if (description) {
      document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", description[language]);
    }
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
      // The language still applies for the current page when storage is unavailable.
    }
  }, [description, language, title]);

  const value = useMemo<I18nContextValue>(() => ({
    language,
    setLanguage,
    t: (chinese, english) => language === "zh" ? chinese : english,
  }), [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error("I18nProvider is unavailable");
  return context;
}

export function LanguageSwitcher({ inverse = false }: { inverse?: boolean }) {
  const { language, setLanguage, t } = useI18n();
  return (
    <div
      className={`language-switcher${inverse ? " language-switcher-inverse" : ""}`}
      role="group"
      aria-label={t("界面语言", "Interface language")}
    >
      <button type="button" className={language === "zh" ? "active" : ""} onClick={() => setLanguage("zh")}>中</button>
      <button type="button" className={language === "en" ? "active" : ""} onClick={() => setLanguage("en")}>EN</button>
    </div>
  );
}
